import { Injectable } from '@angular/core';
import MapView from '@arcgis/core/views/MapView';
import Layer from '@arcgis/core/layers/Layer';
import WMSLayer from '@arcgis/core/layers/WMSLayer';
import WFSLayer from '@arcgis/core/layers/WFSLayer';
import GeoJSONLayer from '@arcgis/core/layers/GeoJSONLayer';
import Graphic from '@arcgis/core/Graphic';
import Point from '@arcgis/core/geometry/Point';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import { OGCServerTypeEnum } from '@gishub/domain';
import { OgcUrlType } from './ogc-state';
import { WfsLayerFactory } from '../../factories/wfs-layer-factory';

/** Capa OGC creada y cargada en el mapa, lista para registrarse en el estado. */
export interface OgcCreatedLayer {
  layer: WMSLayer | WFSLayer | GeoJSONLayer;
  type: 'wms' | 'wfs';
  title: string;
}

/**
 * Encapsula la interacción ArcGIS del módulo OGC.
 * Delega la creación de capas WFS a WfsLayerFactory.
 * El notifier solo orquesta estado y delega.
 */
@Injectable()
export class OgcService {
  private view: MapView | null = null;

  setView(view: MapView): void {
    this.view = view;
  }

  get hasView(): boolean {
    return !!this.view;
  }

  /**
   * Crea las capas WMS/WFS seleccionadas, las agrega al mapa, espera su carga
   * y descarta las que fallan. Devuelve solo las que quedaron cargadas.
   */
  async addServiceLayers(
    urlType: OgcUrlType,
    serviceUrl: string,
    hostTitle: string,
    selected: { name: string; title: string }[],
  ): Promise<OgcCreatedLayer[]> {
    if (!this.view?.map) throw new Error('No hay vista de mapa activa.');

    if (urlType === OGCServerTypeEnum.WMS) {
      return this.createWmsLayers(serviceUrl, hostTitle, selected);
    }

    return this.createWfsLayers(serviceUrl, selected);
  }

  // ─── WMS ────────────────────────────────────────────────────────────────────

  private createWmsLayers(
    serviceUrl: string,
    hostTitle: string,
    selected: { name: string; title: string }[],
  ): OgcCreatedLayer[] {
    const layer = new WMSLayer({
      url: serviceUrl,
      title: hostTitle,
      sublayers: selected.map(l => ({ name: l.name })),
    });
    this.view!.map?.add(layer);
    return [{ layer, type: 'wms', title: `WMS – ${selected.map(l => l.title).join(', ')}` }];
  }

  // ─── WFS ────────────────────────────────────────────────────────────────────

  /**
   * Crea capas WFS usando WfsLayerFactory (nativo + fallback GeoJSON).
   */
  private async createWfsLayers(
    serviceUrl: string,
    selected: { name: string; title: string }[],
  ): Promise<OgcCreatedLayer[]> {
    const results = await Promise.allSettled(
      selected.map(sel =>
        WfsLayerFactory.create(serviceUrl, sel.name, sel.title, this.view!),
      ),
    );

    const created: OgcCreatedLayer[] = [];
    for (const result of results) {
      if (result.status === 'fulfilled' && result.value) {
        created.push({
          layer: result.value.layer,
          type: 'wfs',
          title: result.value.title,
        });
      }
    }
    return created;
  }

  // ─── GeoJSON (archivos subidos) ─────────────────────────────────────────────

  /** Crea un GeoJSONLayer desde un FeatureCollection procesado, lo carga y hace zoom. */
  async addGeoJsonLayer(geojson: any, title: string): Promise<GeoJSONLayer> {
    if (!this.view?.map) throw new Error('No hay vista de mapa activa.');

    const blob = new Blob([JSON.stringify(geojson)], { type: 'application/json' });
    const blobUrl = URL.createObjectURL(blob);
    const layer = new GeoJSONLayer({ url: blobUrl, title });
    this.view.map.add(layer);

    try {
      await layer.load();
    } catch {
      this.view.map.remove(layer);
      throw new Error('la capa no pudo cargarse en el mapa');
    }

    if (layer.fullExtent) this.view.goTo(layer.fullExtent).catch(() => {});
    return layer;
  }

  // ─── Puntos manuales ────────────────────────────────────────────────────────

  addPoint(lat: number, lng: number, label: string): Graphic | null {
    if (!this.view) return null;

    const point = new Point({ longitude: lng, latitude: lat });
    const symbol = new SimpleMarkerSymbol({
      color: '#007C4B',
      size: '12px',
      outline: { color: [255, 255, 255], width: 2 },
    });
    const graphic = new Graphic({
      geometry: point,
      symbol,
      attributes: { label, lat, lng },
      popupTemplate: {
        title: label || 'Coordenada manual',
        content: `Lat: ${lat}<br>Lng: ${lng}`,
      },
    });

    this.view.graphics.add(graphic);
    this.view.goTo({ target: point, zoom: 12 });
    return graphic;
  }

  // ─── Utilidades de capa ─────────────────────────────────────────────────────

  removeLayer(layerRef: Layer): void {
    this.view?.map?.remove(layerRef);
  }

  removeGraphic(graphic: Graphic): void {
    this.view?.graphics?.remove(graphic);
  }

  setVisibility(layerRef: Layer | Graphic, visible: boolean): void {
    layerRef.visible = visible;
  }

  setOpacity(layerRef: Layer, opacity: number): void {
    layerRef.opacity = opacity;
  }

  zoomToLayer(layerRef: Layer): void {
    if (layerRef.fullExtent) this.view?.goTo(layerRef.fullExtent).catch(() => {});
  }

  zoomToPoint(graphic: Graphic): void {
    if (graphic.geometry) this.view?.goTo({ target: graphic.geometry, zoom: 14 }).catch(() => {});
  }
}
